import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Form, { Validations } from '../../../../../components/Form';
import useRequest from '../../../../../hooks/useRequest';
import { ControlItem } from '../../../types';
import { formatParam } from '../../helpers';

import ServicePaths from '../../../../../bridge/utils/ServicePaths';

const noPasswordMessage = {
  success: false,
  message: 'To use this feature, the user must have set a password',
};

const PanicSellForm = () => {
  const [userId, setUserId] = useState<string | undefined>();
  const [password, setPassword] = useState<string | undefined>();
  const [useMultiOrders, setUseMultiOrders] = useState<string | undefined>();
  const [canFetchUser, setCanFetchUser] = useState(false);
  const [errors, setErrors] = useState<Record<string, Validations>>({});
  const [formMessage, setFormMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const params = useMemo(() => ({ userId }), [userId]);
  const options = useMemo(
    () => ({
      shouldFetch: canFetchUser,
    }),
    [canFetchUser],
  );

  const { data: userData } = useRequest(
    ServicePaths.CONTROL.GetUser,
    params,
    options,
  );

  useEffect(() => {
    !!userData && setIsLoading(false);
  }, [userData]);

  const checkUserIdValidation = useCallback(() => {
    /** userId can not be empty */
    let didError;

    setErrors({});

    if (typeof userId === 'undefined' || userId?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        userId: Validations.Required,
      }));

      didError = true;
    }

    return !didError;
  }, [userId]);

  const handleGetUserSubmit = useCallback(() => {
    setIsLoading(true);
    const canContinue = checkUserIdValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    setCanFetchUser(true);
  }, [checkUserIdValidation]);

  const checkPanicSellValidation = useCallback(() => {
    /** password can not be empty */
    let didError;

    setErrors({});

    if (typeof password === 'undefined' || password?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        password: Validations.Required,
      }));

      didError = true;
    }

    if (useMultiOrders !== 'true' && useMultiOrders !== 'false') {
      setErrors((prev) => ({
        ...prev,
        useMultiOrders: Validations.BooleanTyping,
      }));

      didError = true;
    }

    return !didError;
  }, [password, useMultiOrders]);

  const handlePanicSellSubmit = useCallback(async () => {
    setIsLoading(true);
    const canContinue = checkPanicSellValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    const { data } = await axios({
      method: 'POST',
      url: ServicePaths.CONTROL.PanicSell,
      params: {
        userId: formatParam(userId),
        password: formatParam(password),
        useMultiOrders: formatParam(useMultiOrders),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [checkPanicSellValidation, password, useMultiOrders, userId]);

  const getUserDataConfig = useMemo(() => {
    return {
      title: ControlItem.PanicSell,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
        },
      ],
      handleSubmit: handleGetUserSubmit,
      isLoading,
    };
  }, [errors.userId, handleGetUserSubmit, isLoading, userId]);

  const panicSellFormConfig = useMemo(() => {
    return {
      title: ControlItem.PanicSell,
      formMessage: noPasswordMessage,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
          isLocked: true,
        },
        {
          label: 'Password',
          value: password,
          handleValueChange: setPassword,
          error: errors.password,
          isSecret: true,
        },
        {
          label: 'Use Multi Orders',
          value: useMultiOrders,
          handleValueChange: setUseMultiOrders,
          error: errors.useMultiOrders,
        },
      ],
      handleSubmit: handlePanicSellSubmit,
      isLoading,
    };
  }, [
    errors.password,
    errors.useMultiOrders,
    errors.userId,
    handlePanicSellSubmit,
    isLoading,
    password,
    useMultiOrders,
    userId,
  ]);

  if (!userData) {
    return <Form config={getUserDataConfig} />;
  }

  return <Form config={panicSellFormConfig} />;
};

export default PanicSellForm;
